<template>
  <div class="garally-page">
    <div class="photos">
      <div v-for="(item, index) in photos" :key="index" class="photos-item">
        <div class="photos-item-frame" :style="getListItemStyle(item)">
          <video
            v-if="isVideoOrAudio(item)"
            :src="item.original_data"
            controls
            class="photos-item-frame-original"
          />
          <div
            v-else-if="!item.original_image"
            class="photos-item-frame-wrapper"
            :style="getPhotoWrapperStyle(item)"
          >
            <PhotoWithFrame
              :image-url="item.image"
              :cast-name="item.cast_name"
              :group-name="item.group_name"
              :open-date="item.open_date"
              :part="item.part"
              :type="item.type"
              :copyright="item.copyright"
              :logo-image="getLogoImage(item)"
              :campaign-text="getCampaignText(item)"
              :frame-image="getFrameImage(item)"
              :back-frame-image="getBackFrameImage(item)"
              :selected-photo="item.frame"
              :is-back-frame="item.frame"
              :is-show-band-text-if-exist-frame="item.need_frame_text"
              :frame-text-color="frameTextColor"
              :frame-background-color="frameBackgroundColor"
              :cast-name-letter-spacing="castNameLetterSpacing"
              :cast-name-margin-right="castNameMarginRight"
            />
          </div>
          <img
            v-else
            :src="item.original_image"
            alt="image"
            class="photos-item-frame-original"
          />
        </div>
        <template v-if="isVideoOrAudio(item)">
          <Button
            class="photos-item-download"
            @click="handleClickDownloadVideo(index)"
            >ダウンロード</Button
          >
        </template>
        <template v-else>
          <Button
            class="photos-item-download"
            @click="handleClickPhotoFrame(index)"
            >拡大する</Button
          >
        </template>
      </div>
    </div>
    <div v-if="videoProgress" class="video-loading">
      <VProgressCircular
        :rotate="360"
        :size="100"
        :width="15"
        :value="videoProgress"
        color="teal"
      >
        {{ videoProgress }}
      </VProgressCircular>
      ダウンロード中
    </div>
  </div>
</template>

<script>
import PhotoWithFrame from '~/components/organisms/PhotoWithFrame'
import Button from '~/components/atoms/Button'
import { GarallyPhotoType } from '~/helpers/garally'

const photoFrameHeight = 130

export default {
  middleware: ['setAccessToken', 'requiredAuth', 'setCurrentUser'],
  components: {
    PhotoWithFrame,
    Button
  },
  async asyncData({ $photoRepository }) {
    const data = await $photoRepository.fetch()
    return { photos: data }
  },
  data: () => ({
    photos: [],
    resizeHandler: undefined,
    windowWidth: 1000,
    selectedIndex: undefined,
    videoProgress: undefined
  }),
  computed: {
    frameTextColor() {
      return this.$route.query.frame_text_color
    },
    frameBackgroundColor() {
      return this.$route.query.frame_background_color
    },
    castNameMarginRight() {
      return this.$route.query.cast_name_margin_right
    },
    castNameLetterSpacing() {
      return this.$route.query.cast_name_letter_spacing
    }
  },
  mounted() {
    this.windowHeight = window.innerHeight
    this.windowWidth = window.innerWidth

    this.resizeHandler = (event) => {
      this.windowHeight = event.target.innerHeight
      this.windowWidth = event.target.innerWidth
    }

    window.addEventListener('resize', this.resizeHandler)
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeHandler)
  },
  methods: {
    isVideoOrAudio(item) {
      return item.mime_type === 'video' || item.mime_type === 'audio'
    },
    getPhotoFrameScale(item) {
      let defaultWidth
      switch (item.type) {
        case GarallyPhotoType.oneShot:
          defaultWidth = 750
          break

        case GarallyPhotoType.oneShotAKB48:
        case GarallyPhotoType.oneShotAKB48_62nd:
        case GarallyPhotoType.oneShotAKB48_63rd:
        case GarallyPhotoType.oneShotAKB48_64th:
        case GarallyPhotoType.oneShotAKB48_10th_AL:
        case GarallyPhotoType.oneShotAKB48_65th:
          defaultWidth = 750
          break

        case GarallyPhotoType.twoShotWide:
        case GarallyPhotoType.twoShot:
          if (item['2shot_band_image']) {
            defaultWidth = 996
          } else {
            defaultWidth = 1000
          }
          break
      }

      const targetWidth =
        (this.windowWidth > 1040 ? 1040 : this.windowWidth) * (49 / 100)
      const scale = targetWidth / defaultWidth
      return scale
    },
    getPhotoWrapperStyle(item) {
      const scale = this.getPhotoFrameScale(item)
      return {
        transform: `scale(${scale})`
      }
    },
    getListItemStyle(item) {
      const scale = this.getPhotoFrameScale(item)
      let defaultHeight

      switch (item.type) {
        case GarallyPhotoType.oneShot:
          defaultHeight = 1000 + photoFrameHeight
          break

        case GarallyPhotoType.oneShotAKB48:
        case GarallyPhotoType.oneShotAKB48_62nd:
        case GarallyPhotoType.oneShotAKB48_63rd:
        case GarallyPhotoType.oneShotAKB48_64th:
        case GarallyPhotoType.oneShotAKB48_10th_AL:
        case GarallyPhotoType.oneShotAKB48_65th:
          defaultHeight = 1000 + photoFrameHeight
          break

        case GarallyPhotoType.twoShotWide:
          defaultHeight = 570
          break

        case GarallyPhotoType.twoShot:
          defaultHeight = 750 + photoFrameHeight
          break
      }

      return {
        height: `${defaultHeight * scale}px`
      }
    },
    async handleClickDownloadVideo(index) {
      try {
        this.videoProgress = 0.1
        const videoUrl = this.photos[index].original_data
        const response = await fetch(videoUrl, { cache: 'no-cache' })
        const reader = response.body.getReader()
        const contentLength = +response.headers.get('Content-Length')

        let receivedLength = 0
        const chunks = []
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            this.videoProgress = undefined
            break
          }

          chunks.push(value)
          receivedLength += value.length

          this.videoProgress = ((receivedLength / contentLength) * 100).toFixed(
            1
          )
        }

        const blob = new Blob(chunks)

        const link = document.createElement('a')

        const fileParts = videoUrl.split('/')
        const fileName = fileParts[fileParts.length - 1]

        link.download = fileName
        link.href = URL.createObjectURL(blob)
        link.click()
      } catch (error) {
        this.$toast.error({ error })
      } finally {
        this.videoProgress = undefined
      }
    },
    handleClickPhotoFrame(index) {
      this.selectedIndex = index
      this.$store.commit('garally/setPhoto', this.photos[this.selectedIndex])
      this.$router.push({
        path: '/garally/photo',
        query: {
          frame_text_color: this.frameTextColor,
          frame_background_color: this.frameBackgroundColor,
          cast_name_margin_right: this.castNameMarginRight,
          cast_name_letter_spacing: this.castNameLetterSpacing
        }
      })
    },
    getLogoImage(photo) {
      switch (photo.type) {
        case GarallyPhotoType.oneShot:
          return photo['1shot_logo']

        case GarallyPhotoType.oneShotAKB48:
        case GarallyPhotoType.oneShotAKB48_62nd:
        case GarallyPhotoType.oneShotAKB48_63rd:
        case GarallyPhotoType.oneShotAKB48_64th:
        case GarallyPhotoType.oneShotAKB48_10th_AL:
        case GarallyPhotoType.oneShotAKB48_65th:
          return photo['1shot_logo']

        case GarallyPhotoType.twoShotWide:
          return photo['2shot_logo']

        case GarallyPhotoType.twoShot:
          return photo['2shot_logo']
      }
    },
    getCampaignText(photo) {
      switch (photo.type) {
        case GarallyPhotoType.oneShot:
          return photo['1shot_text']

        case GarallyPhotoType.oneShotAKB48:
        case GarallyPhotoType.oneShotAKB48_62nd:
        case GarallyPhotoType.oneShotAKB48_63rd:
        case GarallyPhotoType.oneShotAKB48_64th:
        case GarallyPhotoType.oneShotAKB48_10th_AL:
        case GarallyPhotoType.oneShotAKB48_65th:
          return photo['1shot_text']

        case GarallyPhotoType.twoShotWide:
          return photo['2shot_text']

        case GarallyPhotoType.twoShot:
          return photo['2shot_text']
      }
    },
    getFrameImage(photo) {
      switch (photo.type) {
        case GarallyPhotoType.oneShot:
          return photo['1shot_band_image']

        case GarallyPhotoType.twoShotWide:
          return photo['2shot_band_image']

        case GarallyPhotoType.twoShot:
          return photo['2shot_band_image']
      }
    },
    getBackFrameImage(photo) {
      switch (photo.type) {
        case GarallyPhotoType.oneShot:
          return photo['1shot_frame_image']

        case GarallyPhotoType.twoShot:
          return photo['2shot_frame_image']
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.garally-page {
  max-width: 1040px;
  margin: 0 auto;
  padding-bottom: 32px;
}

.photos {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  &-item {
    width: calc(50% - 8px);
    margin-bottom: 16px;
    &-frame {
      &-wrapper {
        transform-origin: top left;
      }

      &-original {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      video {
        border: 1px solid #333333;
      }
    }

    &-download {
      width: 50%;
      margin: 8px auto 0;
    }
  }
}

.video-loading {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff66;
  flex-direction: column;
}
</style>
